// src/components/wallet/SparksFly.tsx
import { useEffect } from "react";

type SparksFlyProps = {
  /** CSS-селектор цели (предпочтительно) */
  target?: string;
  /** Поддержка старого имени пропса, чтобы не править места вызова */
  targetSelector?: string;
  /** сколько звёзд запустить */
  count?: number;
  /** длительность полёта одной звезды (мс) */
  durationMs?: number;
  /** радиус фонтанного разброса (px) */
  spread?: number;
  /** коллбек по завершении всей анимации */
  onDone?: () => void;
  /** размер звезды (px) */
  size?: number;
  /** максимальный угол вращения в полёте (deg) */
  spinDeg?: number;
  /** высота параболической дуги (доля от дистанции до цели) */
  arc?: number;
};

// 5-конечная звезда в viewBox 0 0 22 22, центр (11,11), R=10, r=4
const STAR_PATH =
  "M11,1 L13.35,7.76 L20.51,7.91 L14.8,12.24 L16.88,19.09 L11,15 L5.12,19.09 L7.2,12.24 L1.49,7.91 L8.65,7.76 Z";

const SVG_NS = "http://www.w3.org/2000/svg";

function ensureStarDefs(): void {
  const DEFS_ID = "spark-star-defs";
  if (document.getElementById(DEFS_ID)) return;

  const wrap = document.createElementNS(SVG_NS, "svg");
  wrap.setAttribute("id", DEFS_ID);
  wrap.style.cssText =
    "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";

  const defs = document.createElementNS(SVG_NS, "defs");

  // Радиальный градиент: янтарь/золото → тёплый розовый
  const grad = document.createElementNS(SVG_NS, "radialGradient");
  grad.setAttribute("id", "spark-star-grad");
  grad.setAttribute("cx", "40%");
  grad.setAttribute("cy", "30%");
  grad.setAttribute("r", "65%");
  const stops: Array<[string, string]> = [
    ["0%", "#fef9c3"],
    ["55%", "#f59e0b"],
    ["100%", "#ec4899"],
  ];
  stops.forEach(([offset, color]) => {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    grad.appendChild(stop);
  });
  defs.appendChild(grad);

  // Glow-фильтр через feGaussianBlur + feMerge
  const filter = document.createElementNS(SVG_NS, "filter");
  filter.setAttribute("id", "spark-star-glow");
  filter.setAttribute("x", "-50%");
  filter.setAttribute("y", "-50%");
  filter.setAttribute("width", "200%");
  filter.setAttribute("height", "200%");
  const blur = document.createElementNS(SVG_NS, "feGaussianBlur");
  blur.setAttribute("stdDeviation", "1.8");
  blur.setAttribute("result", "blur");
  const merge = document.createElementNS(SVG_NS, "feMerge");
  const n1 = document.createElementNS(SVG_NS, "feMergeNode");
  n1.setAttribute("in", "blur");
  const n2 = document.createElementNS(SVG_NS, "feMergeNode");
  n2.setAttribute("in", "SourceGraphic");
  merge.appendChild(n1);
  merge.appendChild(n2);
  filter.appendChild(blur);
  filter.appendChild(merge);
  defs.appendChild(filter);

  wrap.appendChild(defs);
  document.body.appendChild(wrap);
}

function ensureBumpStyles(): void {
  const STYLE_ID = "spark-fly-style";
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .spark-bump {
      animation: spark-bump 280ms cubic-bezier(.36,.07,.19,.97) 1;
      will-change: transform;
    }
    @keyframes spark-bump {
      0%   { transform: translateZ(0) scale(1); }
      35%  { transform: translateZ(0) scale(1.18); }
      65%  { transform: translateZ(0) scale(0.95); }
      100% { transform: translateZ(0) scale(1); }
    }
  `;
  document.head.appendChild(style);
}

export default function SparksFly({
  target,
  targetSelector,
  count = 8,
  durationMs = 800,
  spread = 24,
  onDone,
  size = 22,
  spinDeg = 270,
  arc = 0.4,
}: SparksFlyProps) {
  const selector = target ?? targetSelector;
  if (!selector) return null;

  useEffect(() => {
    // prefers-reduced-motion: только мягкий bump кошелька, без полёта
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const targetEl = document.querySelector<HTMLElement>(selector);
      if (targetEl) {
        ensureBumpStyles();
        targetEl.classList.remove("spark-bump");
        void targetEl.offsetWidth;
        targetEl.classList.add("spark-bump");
        targetEl.addEventListener(
          "animationend",
          () => targetEl.classList.remove("spark-bump"),
          { once: true }
        );
      }
      onDone?.();
      return;
    }

    const targetEl = document.querySelector<HTMLElement>(selector);
    if (!targetEl) return;

    ensureStarDefs();
    ensureBumpStyles();

    const startX = window.innerWidth / 2;
    const startY = window.innerHeight / 2;

    const trg = targetEl.getBoundingClientRect();
    const endX = trg.left + trg.width / 2;
    const endY = trg.top + trg.height / 2;

    const dx = endX - startX;
    const dy = endY - startY;
    const dist = Math.hypot(dx, dy) || 1;

    let finished = 0;
    let cancelled = false;
    const particles: SVGSVGElement[] = [];
    const timers: ReturnType<typeof setTimeout>[] = [];

    const bumpTarget = () => {
      targetEl.classList.remove("spark-bump");
      void targetEl.offsetWidth;
      targetEl.classList.add("spark-bump");
      targetEl.addEventListener(
        "animationend",
        () => targetEl.classList.remove("spark-bump"),
        { once: true }
      );
    };

    const makeParticle = () => {
      // SVG-звезда с inline-градиентом и glow
      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("width", String(size));
      svg.setAttribute("height", String(size));
      svg.setAttribute("viewBox", "0 0 22 22");

      const starPath = document.createElementNS(SVG_NS, "path");
      starPath.setAttribute("d", STAR_PATH);
      starPath.setAttribute("fill", "url(#spark-star-grad)");
      starPath.setAttribute("filter", "url(#spark-star-glow)");
      svg.appendChild(starPath);

      svg.style.position = "fixed";
      svg.style.left = `${startX}px`;
      svg.style.top = `${startY}px`;
      svg.style.pointerEvents = "none";
      svg.style.zIndex = "9999";
      svg.style.willChange = "transform, opacity";

      document.body.appendChild(svg);
      particles.push(svg);

      // Фонтанный разброс: случайный угол вылета, сходящийся к цели
      const blastAngle = Math.random() * 2 * Math.PI;
      const blastR = spread * (0.4 + Math.random() * 0.6);
      const blastX = Math.cos(blastAngle) * blastR;
      const blastY = Math.sin(blastAngle) * blastR;

      // Случайное вращение за полёт
      const spin = (Math.random() * 2 - 1) * spinDeg;

      // Параболическая дуга: горизонталь линейна к цели,
      // вертикаль добавляет горб Math.sin(t*π)*arc*dist
      const arcH = arc * dist;
      const kx = (t: number) => dx * t + blastX * (1 - t);
      const ky = (t: number) =>
        dy * t + blastY * (1 - t) - Math.sin(t * Math.PI) * arcH;

      const dur = durationMs + Math.random() * 160;

      const keyframes: Keyframe[] = [
        {
          transform: `translate(calc(-50% + ${kx(0)}px), calc(-50% + ${ky(0)}px)) scale(0.6) rotate(0deg)`,
          opacity: 0.85,
          offset: 0,
        },
        {
          transform: `translate(calc(-50% + ${kx(0.3)}px), calc(-50% + ${ky(0.3)}px)) scale(0.9) rotate(${spin * 0.3}deg)`,
          opacity: 1,
          offset: 0.3,
        },
        {
          transform: `translate(calc(-50% + ${kx(0.6)}px), calc(-50% + ${ky(0.6)}px)) scale(1.1) rotate(${spin * 0.65}deg)`,
          opacity: 1,
          offset: 0.6,
        },
        {
          // overshoot — звезда «влетает» в кошелёк крупнее
          transform: `translate(calc(-50% + ${kx(0.82)}px), calc(-50% + ${ky(0.82)}px)) scale(1.3) rotate(${spin * 0.9}deg)`,
          opacity: 0.95,
          offset: 0.82,
        },
        {
          // всасывание в кошелёк
          transform: `translate(calc(-50% + ${kx(1)}px), calc(-50% + ${ky(1)}px)) scale(0.35) rotate(${spin}deg)`,
          opacity: 0,
          offset: 1,
        },
      ];

      const anim = svg.animate(keyframes, {
        duration: dur,
        easing: "cubic-bezier(.22,.61,.36,1)",
        fill: "forwards",
      });

      anim.onfinish = () => {
        if (cancelled) return;
        svg.remove();
        finished++;
        bumpTarget();
        if (finished >= count) onDone?.();
      };
    };

    for (let i = 0; i < count; i++) {
      const t = setTimeout(() => makeParticle(), i * 45);
      timers.push(t);
    }

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      particles.forEach((p) => p.remove());
      targetEl.classList.remove("spark-bump");
    };
  }, [selector, count, durationMs, spread, onDone, size, spinDeg, arc]);

  return null;
}
